import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, Users, Clock, CheckCircle, XCircle,
  ThumbsUp, Send, Plus, Filter, Search
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const CostCollaboration = () => {
  const { shouldShowData } = useDemoData();
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const { token, user } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchDiscussions();
  }, []);

  const fetchDiscussions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/collaboration/discussions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDiscussions(data.discussions || []);
    } catch (error) {
      console.error('Error fetching discussions:', error);
    } finally {
      setLoading(false);
    }
  };

  const addComment = async (resourceId) => {
    if (!newComment.trim()) return;
    
    try {
      await fetch(`${API_URL}/api/collaboration/discussions/comment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          resource_id: resourceId,
          message: newComment
        })
      });
      setNewComment('');
      fetchDiscussions();
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved': return 'bg-green-100 text-green-600';
      case 'under_review': return 'bg-yellow-100 text-yellow-600';
      case 'pending': return 'bg-emerald-100 text-emerald-600';
      default: return 'bg-gray-100 text-slate-400';
    }
  };

  const getDecisionIcon = (decision) => {
    switch (decision) {
      case 'approved': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'rejected': return <XCircle className="w-5 h-5 text-red-500" />;
      default: return <Clock className="w-5 h-5 text-yellow-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="collaboration-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!shouldShowData) return <EmptyDataState title="Cost Collaboration" description="Connect your cloud accounts to enable team-based cost discussions and approvals." />;
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="cost-collaboration-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white"><BrandTitle>Cost Collaboration</BrandTitle></h1>
          <p className="text-slate-400 dark:text-gray-400 mt-1">
            Real-time collaboration on cost decisions - like Google Docs for your cloud spend
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            toast.info('Collaboration discussions launching in the next release — watch the changelog!', {
              description: 'Meanwhile, you can @mention teammates from any cost detail page.',
              duration: 5000,
            });
          }}
          data-testid="start-discussion-btn"
          className="flex items-center gap-2 bg-emerald-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Start Discussion
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <MessageSquare className="w-5 h-5 text-emerald-500" />
            <span className="text-slate-400 dark:text-gray-400">Active Discussions</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {discussions.filter(d => d.status === 'under_review').length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-slate-400 dark:text-gray-400">Resolved This Month</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {discussions.filter(d => d.status === 'resolved').length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <ThumbsUp className="w-5 h-5 text-emerald-500" />
            <span className="text-slate-400 dark:text-gray-400">Savings from Discussions</span>
          </div>
          <p className="text-3xl font-bold text-green-500">
            ${discussions.reduce((sum, d) => sum + (d.savings_realized || 0), 0).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Discussions List */}
      <div className="space-y-4">
        {discussions.map((discussion) => (
          <div 
            key={discussion.id}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden"
            data-testid={`discussion-${discussion.id}`}
          >
            {/* Discussion Header */}
            <div className="p-6 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    {discussion.resource}
                  </h3>
                  <p className="text-slate-400 dark:text-gray-400 text-sm">
                    Resource ID: {discussion.resource_id}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(discussion.status)}`}>
                    {discussion.status.replace('_', ' ')}
                  </span>
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">
                    ${discussion.cost?.toLocaleString()}
                  </span>
                </div>
              </div>
              
              {/* Participants */}
              <div className="flex items-center gap-2 mt-4">
                <Users className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-slate-400 dark:text-gray-400">
                  {discussion.participants?.join(', ')}
                </span>
              </div>
            </div>

            {/* Comments Thread */}
            <div className="p-6 bg-gray-50 dark:bg-gray-700/30">
              <div className="space-y-4">
                {discussion.comments?.map((comment, index) => (
                  <div key={index} className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-400 bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center text-white text-sm font-bold">
                      {comment.author?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900 dark:text-white text-sm">
                          {comment.author}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(comment.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 p-3 rounded-lg">
                        {comment.message}
                      </p>
                      <div className="flex gap-1 mt-2">
                        {comment.reactions?.map((reaction, i) => (
                          <span key={i} className="text-sm">{reaction}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Comment */}
              <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                <div className="w-8 h-8 rounded-full bg-green-400 bg-gradient-to-br from-green-400 to-teal-500 flex items-center justify-center text-white text-sm font-bold">
                  {user?.name?.charAt(0) || 'Y'}
                </div>
                <div className="flex-1 flex gap-2">
                  <input
                    type="text"
                    placeholder="Add a comment..."
                    value={selectedDiscussion === discussion.id ? newComment : ''}
                    onChange={(e) => {
                      setSelectedDiscussion(discussion.id);
                      setNewComment(e.target.value);
                    }}
                    className="flex-1 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={() => addComment(discussion.resource_id)}
                    className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Decision Footer */}
            {discussion.decision && (
              <div className={`px-6 py-3 flex items-center justify-between ${
                discussion.decision === 'approved' ? 'bg-green-50 dark:bg-green-900/20' : 
                discussion.decision === 'rejected' ? 'bg-red-50 dark:bg-red-900/20' : 
                'bg-yellow-50 dark:bg-yellow-900/20'
              }`}>
                <div className="flex items-center gap-2">
                  {getDecisionIcon(discussion.decision)}
                  <span className="font-medium capitalize">
                    {discussion.decision}
                  </span>
                </div>
                {discussion.savings_realized > 0 && (
                  <span className="text-green-600 font-bold">
                    Saved ${discussion.savings_realized.toLocaleString()}/month
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {discussions.length === 0 && (
        <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl">
          <MessageSquare className="w-16 h-16 text-gray-300 dark:text-slate-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No Active Discussions</h3>
          <p className="text-slate-400 dark:text-gray-400">
            Start a discussion on any cost item to collaborate with your team
          </p>
        </div>
      )}
    </div>
  );
};

export default CostCollaboration;
